import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { BookButton } from "./Button";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50 bg-cream border-b border-ink"
        data-testid="site-nav"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link
            to="/"
            data-testid="nav-home-logo"
            className="text-sm font-medium uppercase leading-[0.95] tracking-tight"
          >
            Greenwich
            <br />
            Village Baths
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-sm uppercase tracking-[0.1em] transition-opacity duration-300 hover:opacity-100 ${
                    isActive ? "opacity-100" : "opacity-60"
                  } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-terracotta after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <BookButton className="hidden px-6 py-3 sm:inline-flex" />
            <button
              className="lg:hidden"
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex flex-col bg-ink text-cream"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-sm uppercase tracking-tight">Menu</span>
              <button aria-label="Close menu" data-testid="mobile-menu-close" onClick={() => setOpen(false)}>
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-5">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.06 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="block border-b border-cream/20 py-4 text-4xl font-medium uppercase tracking-tight"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-5 pb-10">
              <BookButton variant="primary" className="w-full" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
